import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Command, Eye, ImageOff, Layers, Radio, Sparkles, Terminal, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleLogin() {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const EXAMPLES = [
    { id: "01", label: "Portfolio for a photographer", tags: "GRID · DARK · TYPE-DRIVEN" },
    { id: "02", label: "SaaS landing for a coding tutor", tags: "SPLIT HERO · CTA · PRICING" },
    { id: "03", label: "Restaurant one-pager with menu", tags: "EDITORIAL · WARM · SERIF" },
    { id: "04", label: "Personal blog with reading list", tags: "MINIMAL · PROSE · WHITE" },
    { id: "05", label: "Product waitlist for AI copilot", tags: "TERMINAL · CTA · MONOCHROME" },
    { id: "06", label: "Wedding invite with RSVP", tags: "GALLERY · TIMELINE · SOFT" },
];

const FEATURES = [
    {
        icon: Terminal,
        title: "Prompt in, site out",
        body: "Describe the site in plain English. Loom writes a complete single-file HTML you can preview instantly.",
    },
    {
        icon: Radio,
        title: "Live iframe canvas",
        body: "Every generation renders in a hard-framed viewport. Watch layout, type and color come together in real time.",
    },
    {
        icon: Layers,
        title: "Iterate with follow-ups",
        body: "‘Make the hero taller. Add a pricing block.’ Loom keeps the whole file in context and returns an updated build.",
    },
    {
        icon: Zap,
        title: "Powered by Claude Sonnet 4.5",
        body: "The strongest model for front-end code, wired up through Emergent’s universal key. No setup, no API dance.",
    },
];

export default function Landing() {
    const { user } = useAuth();
    const [showcaseItems, setShowcaseItems] = useState([]);
    const [heroCarousel, setHeroCarousel] = useState([]);
    const carouselRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        // Hero: most-popular showcased sites (used as a scrollable strip in the hero)
        fetch(`${API}/showcase?limit=8&sort=popular`)
            .then((r) => (r.ok ? r.json() : { items: [] }))
            .then((d) => { if (!cancelled) setHeroCarousel(d.items || []); })
            .catch(() => { if (!cancelled) setHeroCarousel([]); });
        // Full grid: recent showcased sites (further down the page for SEO)
        fetch(`${API}/showcase?limit=6&sort=recent`)
            .then((r) => (r.ok ? r.json() : { items: [] }))
            .then((d) => { if (!cancelled) setShowcaseItems(d.items || []); })
            .catch(() => { if (!cancelled) setShowcaseItems([]); });
        return () => { cancelled = true; };
    }, []);

    // Mouse-drag horizontal scroll for desktop (touch already works natively via overflow-x-auto)
    const attachDrag = (el) => {
        carouselRef.current = el;
        if (!el || el.__loomDragAttached) return;
        el.__loomDragAttached = true;
        let isDown = false;
        let startX = 0;
        let startScroll = 0;
        const onDown = (e) => {
            // Only left-click; ignore drags starting on <a> so tap-to-open still works
            if (e.button !== 0) return;
            isDown = true;
            startX = e.pageX;
            startScroll = el.scrollLeft;
            el.classList.add("cursor-grabbing");
        };
        const onMove = (e) => {
            if (!isDown) return;
            const dx = e.pageX - startX;
            if (Math.abs(dx) > 4) e.preventDefault();
            el.scrollLeft = startScroll - dx;
        };
        const onUp = () => {
            isDown = false;
            el.classList.remove("cursor-grabbing");
        };
        el.addEventListener("mousedown", onDown);
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
    };

    return (
        <div className="min-h-screen bg-background text-foreground">
            {/* Nav */}
            <header
                data-testid="landing-nav"
                className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur"
            >
                <div className="mx-auto flex max-w-[1400px] items-center justify-between px-6 md:px-10 h-16">
                    <Link to="/" className="flex items-center gap-3">
                        <div className="w-7 h-7 border border-foreground grid place-items-center">
                            <div className="w-2 h-2 bg-accent" />
                        </div>
                        <span className="font-mono text-[13px] tracking-[0.2em] uppercase">Loom / v0.1</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                        <a href="#work" className="hover:text-foreground transition-colors">▍ Work</a>
                        <a href="#how" className="hover:text-foreground transition-colors">▍ Method</a>
                        <a href="#pricing" className="hover:text-foreground transition-colors">▍ Pricing</a>
                    </nav>
                    {user ? (
                        <Link
                            data-testid="nav-open-app"
                            to="/dashboard"
                            className="btn-brutal !h-9 !px-4 !text-[11px]"
                        >
                            Open app <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                        </Link>
                    ) : (
                        <button
                            data-testid="nav-sign-in"
                            onClick={startGoogleLogin}
                            className="btn-brutal !h-9 !px-4 !text-[11px]"
                        >
                            Sign in <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    )}
                </div>
            </header>

            {/* Hero */}
            <section className="relative border-b border-border overflow-hidden">
                <div className="absolute inset-0 grid-lines opacity-[0.25] pointer-events-none" />
                <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 pt-20 md:pt-28 pb-16 md:pb-24 grid grid-cols-12 gap-8">
                    <div className="col-span-12 lg:col-span-8">
                        <div className="overline mb-8">
                            ▍ A build tool from the future · Est. 2026
                        </div>
                        <h1 className="text-[44px] leading-[0.95] sm:text-6xl lg:text-[92px] tracking-tighter font-black">
                            Describe a<br />
                            website.<br />
                            <span className="text-accent">Ship</span> it in{" "}
                            <span
                                className="italic font-light text-foreground/90"
                                style={{ fontFamily: "'Fraunces', serif", fontWeight: 300 }}
                            >
                                90 seconds.
                            </span>
                        </h1>
                        <p className="mt-8 max-w-xl text-muted-foreground text-base md:text-lg leading-relaxed">
                            Loom is a builder for people who think in sentences, not tags. Write a
                            prompt, we return a self-contained site rendered live in an iframe. Then
                            iterate — pixel by pixel, sentence by sentence.
                        </p>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                data-testid="hero-primary-cta"
                                onClick={user ? () => (window.location.href = "/dashboard") : startGoogleLogin}
                                className="btn-brutal"
                            >
                                {user ? "Open the builder" : "Start building"}
                                <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <a
                                href="#work"
                                className="btn-outline-brutal"
                                data-testid="hero-secondary-cta"
                            >
                                See what it makes
                            </a>
                        </div>

                        <div className="mt-14 flex items-center gap-6 font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-accent" /> Claude Sonnet 4.5
                            </span>
                            <span className="hidden md:flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-foreground" /> Google sign-in
                            </span>
                            <span className="flex items-center gap-2">
                                <span className="w-1.5 h-1.5 bg-foreground" /> No credit card
                            </span>
                        </div>
                    </div>

                    {/* Right side — hero canvas */}
                    <div className="col-span-12 lg:col-span-4">
                        {heroCarousel.length > 0 ? (
                            <div
                                data-testid="hero-carousel-wrap"
                                className="border border-border bg-card p-1 shadow-[0_20px_80px_-20px_hsl(14_100%_62%/0.35),0_0_0_1px_hsl(14_100%_62%/0.2)] rounded-md"
                            >
                                <div className="flex items-center justify-between border-b border-border px-3 h-8 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>▍ built.on.loom · swipe</span>
                                    <span className="flex items-center gap-2">
                                        <span className="text-accent">● {heroCarousel.length} live</span>
                                    </span>
                                </div>
                                <div
                                    ref={attachDrag}
                                    data-testid="hero-carousel"
                                    className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth loom-scroll cursor-grab select-none"
                                    style={{ WebkitOverflowScrolling: "touch" }}
                                >
                                    {heroCarousel.map((item, i) => (
                                        <a
                                            key={item.project_id || i}
                                            data-testid={`hero-carousel-card-${i}`}
                                            href={item.public_url}
                                            target="_blank"
                                            rel="noreferrer"
                                            draggable={false}
                                            onDragStart={(e) => e.preventDefault()}
                                            className="group snap-start shrink-0 w-full flex flex-col"
                                        >
                                            <div className="relative aspect-[3/4] w-full overflow-hidden bg-secondary">
                                                {item.og_image_url ? (
                                                    <img
                                                        src={item.og_image_url}
                                                        alt={item.name}
                                                        loading={i === 0 ? "eager" : "lazy"}
                                                        draggable={false}
                                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full grid place-items-center">
                                                        <ImageOff className="w-8 h-8 text-muted-foreground" strokeWidth={1.25} />
                                                    </div>
                                                )}
                                                <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white bg-black/70 backdrop-blur-sm px-2 py-1 rounded-sm">
                                                    {String(i + 1).padStart(2, "0")} / {String(heroCarousel.length).padStart(2, "0")}
                                                </div>
                                                {item.views_total > 0 && (
                                                    <div className="absolute top-3 right-3 inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.16em] text-white bg-primary/85 backdrop-blur-sm px-2 py-1 rounded-sm">
                                                        <Eye className="w-3 h-3" strokeWidth={1.75} />
                                                        {item.views_total.toLocaleString()}
                                                    </div>
                                                )}
                                                <div className="absolute bottom-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <div className="inline-flex items-center gap-1 h-7 px-2 bg-primary text-primary-foreground font-mono text-[9px] uppercase tracking-[0.16em] rounded-sm">
                                                        Open <ArrowUpRight className="w-3 h-3" strokeWidth={1.75} />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="border-t border-border p-3 font-mono text-[11px] leading-relaxed">
                                                <div className="font-black tracking-tight text-sm truncate text-foreground group-hover:text-primary transition-colors">
                                                    {item.name}
                                                </div>
                                                {item.prompt && (
                                                    <div className="mt-1 text-muted-foreground truncate">
                                                        <span className="text-accent">▍</span> prompt: &ldquo;{item.prompt}&rdquo;
                                                    </div>
                                                )}
                                            </div>
                                        </a>
                                    ))}
                                </div>
                                <div className="border-t border-border px-3 h-8 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>◂ swipe · drag ▸</span>
                                    <span>ranked by views</span>
                                </div>
                            </div>
                        ) : (
                            <div className="border border-border bg-card p-1 shadow-[0_20px_80px_-20px_hsl(14_100%_62%/0.35),0_0_0_1px_hsl(14_100%_62%/0.2)] rounded-md">
                                <div className="flex items-center justify-between border-b border-border px-3 h-8 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    <span>▍ preview.iframe</span>
                                    <span className="flex gap-1.5">
                                        <span className="w-1.5 h-1.5 bg-muted-foreground" />
                                        <span className="w-1.5 h-1.5 bg-muted-foreground" />
                                        <span className="w-1.5 h-1.5 bg-accent" />
                                    </span>
                                </div>
                                <div className="aspect-[3/4] w-full overflow-hidden">
                                    <img
                                        src="https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                                        alt="Structural blueprint"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                                <div className="border-t border-border p-3 font-mono text-[11px] leading-relaxed">
                                    <span className="text-accent">▍</span> prompt: &ldquo;portfolio for a
                                    brutalist studio in Milan — dark, mono, hard edges&rdquo;
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Method */}
            <section id="how" className="border-b border-border">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-4">
                        <div className="overline mb-4">▍ 001 · Method</div>
                        <h2 className="text-4xl md:text-5xl font-black tracking-tighter">
                            Four moves. <br />
                            No scaffolding.
                        </h2>
                    </div>
                    <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-border border border-border">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <div key={f.title} className="p-8 border-b border-border sm:border-b sm:last:border-b-0 group">
                                    <div className="flex items-center justify-between">
                                        <Icon className="w-5 h-5" strokeWidth={1.5} />
                                        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            {String(i + 1).padStart(2, "0")}
                                        </span>
                                    </div>
                                    <h3 className="mt-8 text-xl font-black tracking-tight">{f.title}</h3>
                                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.body}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Examples grid */}
            <section id="work" className="border-b border-border">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
                    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                        <div>
                            <div className="overline mb-4">▍ 002 · Prompts in the wild</div>
                            <h2 className="text-4xl md:text-5xl font-black tracking-tighter max-w-2xl">
                                Six people typed six sentences. This is what Loom shipped.
                            </h2>
                        </div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                            ▍ Generated live · No templates
                        </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 border border-border">
                        {EXAMPLES.map((e, i) => (
                            <div
                                key={e.id}
                                data-testid={`example-card-${e.id}`}
                                className={`p-8 border-border ${
                                    i % 3 !== 2 ? "lg:border-r" : ""
                                } ${i % 2 !== 1 ? "sm:border-r lg:border-r" : ""} ${
                                    i < EXAMPLES.length - (EXAMPLES.length % 3 || 3)
                                        ? "lg:border-b"
                                        : ""
                                } border-b group hover:bg-secondary transition-colors duration-150`}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                        prompt / {e.id}
                                    </span>
                                    <Sparkles className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" strokeWidth={1.5} />
                                </div>
                                <div className="mt-10 min-h-[72px] font-mono text-base leading-relaxed">
                                    “{e.label}”
                                </div>
                                <div className="mt-14 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                    {e.tags}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Showcase gallery — real published Loom sites */}
            {showcaseItems.length > 0 && (
                <section
                    id="showcase"
                    data-testid="landing-showcase-section"
                    className="border-b border-border"
                >
                    <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-20 md:py-28">
                        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
                            <div>
                                <div className="overline mb-4">▍ 002.5 · Loom Showcase</div>
                                <h2 className="text-4xl md:text-5xl font-black tracking-tighter max-w-2xl">
                                    Real sites, shipped this week by real Loom users.
                                </h2>
                            </div>
                            <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                                ▍ Live · Auto-updated
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {showcaseItems.map((item, i) => (
                                <a
                                    key={item.project_id || i}
                                    data-testid={`showcase-card-${i}`}
                                    href={item.public_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="group border border-border overflow-hidden hover:border-primary transition-colors bg-secondary/40"
                                >
                                    <div className="relative aspect-[1.91/1] bg-secondary overflow-hidden">
                                        {item.og_image_url ? (
                                            <img
                                                src={item.og_image_url}
                                                alt={item.name}
                                                loading="lazy"
                                                className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                                            />
                                        ) : (
                                            <div className="w-full h-full grid place-items-center">
                                                <ImageOff className="w-6 h-6 text-muted-foreground" strokeWidth={1.25} />
                                            </div>
                                        )}
                                        <div className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.24em] text-white bg-black/60 backdrop-blur-sm px-2 py-1 rounded-sm">
                                            {String(i + 1).padStart(2, "0")}
                                        </div>
                                    </div>
                                    <div className="p-4 border-t border-border flex items-center justify-between gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="font-black tracking-tight text-base truncate">
                                                {item.name}
                                            </div>
                                            {item.prompt && (
                                                <div className="mt-1 font-mono text-[10px] text-muted-foreground uppercase tracking-[0.16em] truncate">
                                                    “{item.prompt}”
                                                </div>
                                            )}
                                        </div>
                                        <ArrowUpRight
                                            className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors shrink-0"
                                            strokeWidth={1.5}
                                        />
                                    </div>
                                </a>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* Split image / manifesto */}
            <section className="border-b border-border">
                <div className="mx-auto max-w-[1400px] grid grid-cols-12">
                    <div className="col-span-12 md:col-span-7 border-r border-border">
                        <img
                            src="https://images.pexels.com/photos/2317640/pexels-photo-2317640.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1200"
                            alt="Solitary figure on a rooftop"
                            className="w-full h-full object-cover min-h-[420px]"
                        />
                    </div>
                    <div className="col-span-12 md:col-span-5 p-10 md:p-16 flex flex-col justify-between">
                        <div>
                            <div className="overline mb-6">▍ 003 · Human in the loop</div>
                            <h2 className="text-3xl md:text-4xl font-black tracking-tighter leading-[1.05]">
                                The AI writes.<br />
                                <span className="text-accent">You direct.</span>
                            </h2>
                            <p className="mt-6 text-sm md:text-base text-muted-foreground leading-relaxed">
                                Every project is a conversation. Ask for a bolder hero. Swap
                                the palette. Add a testimonial block. Loom holds the whole
                                file in memory and returns a fully-updated build every time.
                            </p>
                        </div>
                        <div className="mt-10 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.2em]">
                            <Command className="w-3.5 h-3.5" strokeWidth={1.5} />
                            <span>K to open the builder</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section id="pricing" className="border-b border-border bg-foreground text-background">
                <div className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-8">
                    <div className="col-span-12 md:col-span-8">
                        <div className="overline mb-6 !text-background/60">▍ 004 · One button</div>
                        <h2 className="text-4xl md:text-6xl font-black tracking-tighter leading-[0.95]">
                            Stop wireframing. <br />
                            Start writing.
                        </h2>
                        <p className="mt-6 max-w-lg text-background/70 text-base md:text-lg leading-relaxed">
                            Sign in with Google, describe your first site, and see it live in
                            under two minutes. Free while in beta.
                        </p>
                    </div>
                    <div className="col-span-12 md:col-span-4 flex md:justify-end items-end">
                        <button
                            data-testid="cta-sign-in"
                            onClick={user ? () => (window.location.href = "/dashboard") : startGoogleLogin}
                            className="inline-flex items-center gap-2 h-14 px-8 bg-accent text-accent-foreground font-mono text-xs uppercase tracking-[0.2em] hover:bg-background hover:text-foreground transition-colors duration-150"
                        >
                            {user ? "Open the builder" : "Continue with Google"}
                            <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="mx-auto max-w-[1400px] px-6 md:px-10 py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    ▍ Loom © 2026 · Built with Claude · Emergent
                </div>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-muted-foreground flex gap-6">
                    <a href="#" className="hover:text-foreground transition-colors">Terms</a>
                    <a href="#" className="hover:text-foreground transition-colors">Privacy</a>
                    <a href="#" className="hover:text-foreground transition-colors">Contact</a>
                </div>
            </footer>
        </div>
    );
}
